import { useEffect, useRef, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Link, useNavigate } from 'react-router-dom';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import { useCreateSuite, useSuiteList } from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import { SuiteError, SuitePagination } from './SuiteShared.tsx';

export default function AutomationSuites() {
  const { orgId, projectId } = useOrgProject();
  return <SuiteList key={JSON.stringify([orgId, projectId])} />;
}

function SuiteList() {
  const { orgId, projectId } = useOrgProject();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [archived, setArchived] = useState(false);
  const [create, setCreate] = useState(false);
  const query = useSuiteList(page, debouncedSearch, archived);
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>Automation Suites</Title>
        <Button disabled={!projectId} onClick={() => setCreate(true)}>
          New Suite
        </Button>
      </Group>
      <Text c="dimmed">
        Organize related automations and their shared design blueprint. Membership does not change
        execution or version pins.
      </Text>
      {!projectId ? (
        <Text>Select a project to manage Suites.</Text>
      ) : (
        <>
          <Group>
            <TextInput
              label="Search Suites"
              maxLength={200}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setPage(1);
              }}
            />
            <Checkbox
              label="Include archived Suites"
              checked={archived}
              onChange={(event) => {
                setArchived(event.currentTarget.checked);
                setPage(1);
              }}
            />
          </Group>
          {!query.isError && !query.isFetchedAfterMount && <Text>Loading Suites…</Text>}
          {query.isError && <SuiteError error={query.error} retry={() => void query.refetch()} />}
          {!query.isError && query.isFetchedAfterMount && query.data && (
            <>
              {!query.data.results.length ? (
                <Text>No Suites found. Create an empty Suite to begin.</Text>
              ) : (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Suite</Table.Th>
                      <Table.Th>Blueprint</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {query.data.results.map((suite) => (
                      <Table.Tr key={suite.id}>
                        <Table.Td>
                          <Anchor component={Link} to={`/automation-suites/${suite.id}`}>
                            {suite.name ?? 'Restricted Suite'}
                          </Anchor>
                          <Text size="sm" c="dimmed">
                            {suite.description}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {suite.shared_text_available
                            ? suite.blueprint
                              ? 'Saved'
                              : 'Not yet written'
                            : 'Access limited'}
                        </Table.Td>
                        <Table.Td>
                          <Badge color={suite.archived_at ? 'gray' : 'blue'}>
                            {suite.archived_at ? 'Archived' : 'Active'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              <SuitePagination data={query.data} onPage={setPage} />
            </>
          )}
        </>
      )}
      {create && projectId && (
        <CreateSuite key={`${orgId}:${projectId}`} close={() => setCreate(false)} />
      )}
    </Stack>
  );
}

function CreateSuite({ close }: { close: () => void }) {
  const { orgId, projectId } = useOrgProject();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const mutation = useCreateSuite();
  const navigate = useNavigate();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const submit = async () => {
    if (!projectId) return;
    try {
      const suite = await mutation.mutateAsync({ orgId, projectId, name, description });
      if (mounted.current) navigate(`/automation-suites/${suite.id}`);
    } catch {
      /* Mutation state supplies the error; preserve the form. */
    }
  };
  return (
    <StyledModal opened onClose={close} title="Create Suite">
      <TextInput
        label="Suite name"
        maxLength={200}
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
        disabled={mutation.isPending}
      />
      <Textarea
        label="Description"
        maxLength={4000}
        value={description}
        onChange={(event) => setDescription(event.currentTarget.value)}
        disabled={mutation.isPending}
      />
      <Text size="sm">You can add members and a blueprint later. An empty Suite is valid.</Text>
      {mutation.isError && <SuiteError error={mutation.error} />}
      <Button disabled={!name.trim()} loading={mutation.isPending} onClick={() => void submit()}>
        Create Suite
      </Button>
    </StyledModal>
  );
}
